import service from "@/services/vote";

const getVoteObject = (id, vote = null, comment = null) => {
  return { e_meeting_agenda_item_id: id, vote, comment };
};

const findVoteItem = (votes, voteId) => {
  return votes?.find(({ e_meeting_agenda_item_id: id }) => id === voteId);
};

const findVoteIndex = (votes, voteId) => {
  return votes.findIndex(
    ({ e_meeting_agenda_item_id: itemId }) => itemId === voteId
  );
};

function initialState() {
  return {
    id: null,
    e_meeting_id: null,
    e_meeting_participation_id: null,
    paper_vote: false,
    files: [],
    new_files: [],
    new_mandates: [],
    mandate_users: [],
    paper_vote_affirmers: [],
    votes: [],
    voted: [],
    group_key: null,
    voter_id: null,
    all_signed: false,
    vote_not_valid: false,
    creator: null,
    signing_id: null,
    signing: null,
  };
}

const vote = {
  namespaced: true,
  state: {
    data: initialState(),
    voteChanged: null,
    participations: [],
    addedMandateFiles: [],
  },

  getters: {
    getVoteData: (state) => {
      return state.data;
    },
    getVoteValue: (state) => (voteId) => {
      return findVoteItem(state.data.votes, voteId)?.vote;
    },
    getVoteComment: (state) => (voteId) => {
      return findVoteItem(state.data.votes, voteId)?.comment;
    },
    getVoteFiles: (state) => {
      return state.data.files;
    },
    getVoteNewFiles: (state) => {
      return state.data.new_files;
    },
    getAddedMandateFiles: (state) => {
      return state.addedMandateFiles;
    },
    getMeetingId: (state) => {
      return state.data.e_meeting_id;
    },
    getId: (state) => {
      return state.data.id;
    },
    getVoterId: (state) => {
      return state.data.voter_id;
    },
    getCreator: (state) => {
      return state.data.creator;
    },
    getValidVote: (state) => {
      return state.data.vote_not_valid;
    },
    getVoterGroupKey: (state) => {
      return state.data.group_key;
    },
    isSigned: (state) => {
      return state.data.all_signed;
    },
    getAffirmStatus: (state) => {
      if (state.data.votes && state.data.votes[0]) {
        return state.data.votes[0].affirmer;
      }
    },
    isVoteFinalized: (state, getters, rootState, rootGetters) => {
      const requiresSigning = rootGetters["meeting/needsDigitalSignature"];

      if (state.data.votes.length === 0) {
        return false;
      }
      let votedUserSigned = false;
      const apartments = state.data.apartments;
      if (apartments) {
        const user = apartments[0].users.filter((e) => e.voted_at !== null);
        if (user) {
          votedUserSigned = user[0]?.user_signed;
        }
      }
      return !(requiresSigning && !votedUserSigned);
    },
    isVoteChanged: (state) => {
      return state.voteChanged;
    },
    getApartmentVotes:
      (state, { rootGetters }) =>
      (group) => {
        let apartmentId = group;
        if (group === "one_owner") {
          const onlyMyApartments = rootGetters["meeting/onlyMine"];
          apartmentId = [onlyMyApartments];
        }
        const apart = state.data?.apartments?.filter(
          (e) => e.cooperative_apartment_id === Number(apartmentId)
        );
        return apart ?? [];
      },
    getMandateFiles: (state) => {
      return state.data.new_mandates;
    },
    getMandateUsers: (state) => {
      return state.data.mandate_users;
    },
    getPaperVoteAffirmers: (state) => {
      return state.data.paper_vote_affirmers;
    },
  },

  mutations: {
    initVotes(state, { items: agendaItems, id }) {
      if (state.data.votes?.length) {
        return;
      }
      agendaItems.forEach(({ id }) => {
        state.data.votes.push(getVoteObject(id));
      });

      state.data.e_meeting_id = id;
    },
    setVoteValue(state, { id, vote }) {
      const itemIndex = findVoteIndex(state.data.votes, id);
      if (itemIndex > -1) {
        state.data.votes[itemIndex].vote = vote;
        if ("id" in state.data.votes[itemIndex]) {
          state.voteChanged = true;
        }
      } else {
        state.data.votes.push({ e_meeting_agenda_item_id: id, vote });
      }
    },
    setVoteComment(state, { id, comment }) {
      const itemIndex = findVoteIndex(state.data.votes, id);
      if (itemIndex > -1) {
        state.data.votes[itemIndex].comment = comment;
      } else {
        state.data.votes.push({ e_meeting_agenda_item_id: id, comment });
      }
    },
    clearVotes(state) {
      const s = initialState();
      Object.keys(s).forEach((key) => {
        state.data[key] = s[key];
      });
    },
    clearOnlyVotesAndFiles(state) {
      state.data.votes = [];
      state.data.new_files = [];
    },
    updateFiles(state, files) {
      state.data.files = files;
    },

    updateVotes(state, value) {
      state.data.voted = value;
    },
    updateVoteFiles(state, files) {
      state.data.new_files = [...files];
    },
    updateAddedMandateFiles(state, files) {
      state.addedMandateFiles = [...files];
    },
    updateMandateFiles(state, mandates) {
      state.data.new_mandates = [...mandates];
    },
    updateMandateUsers(state, users) {
      state.data.mandate_users = users;
    },
    updatePaperVoteAffirmers(state, users) {
      state.data.paper_vote_affirmers = users;
    },
    setVoteData(state, { voteData, groupId, onlyMyApartments }) {
      state.participations = voteData;
      state.data.group_key = groupId;
      state.voteChanged = false;
      let votedValues = {};

      if (voteData.length) {
        if (groupId === "one_owner" || groupId === undefined) {
          votedValues = voteData.find((partisipation) => {
            for (const apartmentId of partisipation.apartment_ids) {
              if (onlyMyApartments.includes(Number(apartmentId))) {
                return true;
              }
            }
            return false;
          });
        } else {
          votedValues = voteData.find((partisipation) => {
            return partisipation.apartment_ids.includes(Number(groupId));
          });
        }
        // if has previous data

        if (votedValues) {
          const voter_id = votedValues.apartments[0].users.find(
            (e) => e.voted_at !== null
          ).user_id;
          state.data = Object.assign(initialState(), votedValues, {
            group_key: groupId,
            voter_id: voter_id,
            mandate_users: votedValues.apartments[0].users
              .map((e) => {
                if (e.user_forwarded_mandate) {
                  return e.user_id;
                }
              })
              .filter(Number),
          });
        }
      }
    },

    votedValues(state, voteData) {
      if (voteData) {
        state.data = Object.assign(initialState(), voteData, {
          paper_vote_affirmers: [],
          mandate_users: voteData?.apartments?.[0].users
            .map((e) => {
              if (e.user_forwarded_mandate) {
                return e.user_id;
              }
            })
            .filter(Number),
        });
      } else {
        state.data.votes = [];
        state.data.files = [];
        state.data.id = null;
      }
    },

    removeVoteFile(state, fileId) {
      state.data.files = state.data.files.filter(({ id }) => id !== fileId);
    },
    setGroupKey(state, key) {
      state.data.group_key = key;
    },
    setVoterId(state, id) {
      state.data.voter_id = id;
    },
  },

  actions: {
    async saveVotes({ commit, state }) {
      try {
        const { id, signing_id } = await service.vote(state.data);
        commit("updateAddedMandateFiles", []);
        return Promise.resolve({ id, signing_id });
      } catch (e) {
        return Promise.reject(e);
      }
    },
    async saveMandateFilesByManager({ commit, state }) {
      try {
        const { id } = await service.MandateFilesByManager(state.data);
        commit("updateAddedMandateFiles", []);
        return Promise.resolve(id);
      } catch (e) {
        return Promise.reject(e);
      }
    },
    async savePaperVotes({ commit, state }) {
      try {
        const { id } = await service.papervote(state.data);
        commit("updateAddedMandateFiles", []);
        return Promise.resolve(id);
      } catch (e) {
        return Promise.reject(e);
      }
    },
    async fetchVotes({ commit }, id) {
      try {
        const voted = await service.getVotes(id);
        commit("updateVotes", voted);
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    },

    async getVote({ commit, rootGetters }, { meetingId, groupId }) {
      // only one owner apartments
      const onlyMyApartments = rootGetters["meeting/onlyMine"];
      try {
        const voteData = await service.getVotes(meetingId);
        commit("setVoteData", { voteData, groupId, onlyMyApartments });
      } catch (e) {
        console.error(e);
      }
    },

    async getUserVotes({ commit }, { meetingId, userId, apartment }) {
      try {
        const voteData = await service.getVotes(meetingId, userId);
        const votedValues = voteData.find((partisipation) => {
          return partisipation.apartment_ids.includes(Number(apartment));
        });
        commit("votedValues", votedValues);
      } catch (e) {
        console.error(e);
      }
    },

    async deleteVoteFile({ commit, getters }, fileId) {
      const meetingId = getters.getMeetingId;
      const voteId = getters.getId;
      try {
        await service.deleteVoteFile(meetingId, voteId, fileId);
        commit("removeVoteFile", fileId);
      } catch (e) {
        return Promise.reject(e);
      }
    },
  },
};

export default vote;
